import React, { useState } from 'react';
import { Sparkles, Lock, Flame } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const DIETS = [
  { key: 'keto',        labelKey: 'dietKeto' },
  { key: 'vegan',       labelKey: 'dietVegan' },
  { key: 'vegetarian',  labelKey: 'dietVegetarian' },
  { key: 'gluten-free', labelKey: 'dietGlutenFree' },
  { key: 'low-carb',    labelKey: 'dietLowCarb' },
  { key: 'dairy-free',  labelKey: 'dietDairyFree' },
];

export default function DietConvert({ recipeId }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState('keto');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!user) { toast.info(t('needLogin')); navigate('/login'); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post('/ai/diet-convert', {
        recipe_id: recipeId, diet: selected, language: lang,
      });
      setResult(data.parsed || { raw: data.raw });
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(t('needPremium'));
        navigate('/premium');
      } else {
        toast.error('Error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="diet-convert-section" className="rounded-3xl bg-white border border-black/5 p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#C05A42]" />
            {t('dietConvert')}
          </h2>
          <p className="text-xs text-[#5C4D44] mt-1">{t('dietConvertHint')}</p>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 bg-[#E29F48] text-white text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 rounded-full">
          <Lock className="h-3 w-3" /> {t('premium')}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {DIETS.map(d => (
          <button
            key={d.key}
            type="button"
            onClick={() => setSelected(d.key)}
            data-testid={`diet-pill-${d.key}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition border ${
              selected === d.key
                ? 'bg-[#C05A42] text-white border-[#C05A42]'
                : 'bg-[#EFEBE1] text-[#2C2420] border-transparent hover:bg-[#E5DECF]'
            }`}
          >
            {t(d.labelKey)}
          </button>
        ))}
      </div>

      <Button
        onClick={run}
        disabled={loading}
        data-testid="diet-convert-btn"
        className="rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white"
      >
        <Sparkles className="h-4 w-4 me-1" />
        {loading ? t('converting') : t('dietConvert')}
      </Button>

      {result && (
        <div className="mt-6 space-y-4" data-testid="diet-convert-result">
          {result.raw ? (
            <pre className="whitespace-pre-wrap text-sm text-[#2C2420]">{result.raw}</pre>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-xl font-display font-semibold text-[#2C2420]">{result.title}</h3>
                {typeof result.kcal === 'number' && (
                  <span className="inline-flex items-center gap-1 bg-[#E29F48] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    <Flame className="h-3 w-3" /> {result.kcal} {t('kcal')}
                  </span>
                )}
              </div>
              {result.summary && <p className="text-sm text-[#5C4D44]">{result.summary}</p>}

              {typeof result.protein === 'number' && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl bg-[#F1F4EE] p-2">
                    <div className="text-base font-display font-bold text-[#4A5D23]">{result.protein}g</div>
                    <div className="text-xs text-[#5C4D44]">{t('protein')}</div>
                  </div>
                  <div className="rounded-xl bg-[#FBF1E0] p-2">
                    <div className="text-base font-display font-bold text-[#C05A42]">{result.carbs}g</div>
                    <div className="text-xs text-[#5C4D44]">{t('carbs')}</div>
                  </div>
                  <div className="rounded-xl bg-[#EFEBE1] p-2">
                    <div className="text-base font-display font-bold text-[#7A8B76]">{result.fat}g</div>
                    <div className="text-xs text-[#5C4D44]">{t('fat')}</div>
                  </div>
                </div>
              )}

              {Array.isArray(result.ingredients) && (
                <div>
                  <h4 className="text-sm font-semibold text-[#2C2420] mb-2">{t('ingredients')}</h4>
                  <ul className="space-y-1 text-sm text-[#2C2420]">
                    {result.ingredients.map((it, i) => <li key={`ing-${i}-${(it || '').slice(0, 20)}`}>• {it}</li>)}
                  </ul>
                </div>
              )}

              {Array.isArray(result.swaps) && result.swaps.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#2C2420] mb-2">{t('swaps')}</h4>
                  <ul className="space-y-1 text-sm text-[#5C4D44]">
                    {result.swaps.map((it, i) => <li key={`swap-${i}-${(it || '').slice(0, 20)}`}>↺ {it}</li>)}
                  </ul>
                </div>
              )}

              {Array.isArray(result.steps) && (
                <div>
                  <h4 className="text-sm font-semibold text-[#2C2420] mb-2">{t('steps')}</h4>
                  <ol className="space-y-1 text-sm text-[#2C2420] list-decimal ps-5">
                    {result.steps.map((it, i) => <li key={`step-${i}-${(it || '').slice(0, 20)}`}>{it}</li>)}
                  </ol>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
